import { FC, useRef, useEffect, useState, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import clsx from "clsx";
import Swal from "sweetalert2";
import { useDataTableStore } from "../../stores/DataTableStore";
import { Merchant } from "../../interfaces/Merchant";
import { updateMerchant } from "../../api/MerchantsCRUD";
import useAccessToken from "../../hooks/AccessToken";
import axios from "axios";
import useSWR, { mutate } from "swr";
import Select from "react-select";
import { City } from "../../interfaces/City";
import { nanoid } from "@reduxjs/toolkit";
import { useSettingsStore } from "../../stores/SettingsStore";
import { CustomField, CustomFieldSerialProps, SettingMultiMerchants } from "../../interfaces/Settings";
import moment from "moment";
import { insertMultiAttachment, insertAttachment } from "../../api/ResourceAPI";
import { KTSVG } from "../../_metronic/helpers";
import useUser from "../../hooks/User";
import { SelectOption } from "../../interfaces/SelectOption";
import { User } from "../../interfaces/User";
import { customThousandSeparatorDot, getDataById, handleCaps, isValidUrl, unformatNumberInput } from "../../functions/general";
import { useIntl } from "react-intl";
import useHandleCloseModal from "../../hooks/HandleCloseModal";
import { useCustomeStore } from "../../stores/CustomeStore";
import { tailspin } from 'ldrs'
import { generateSerialApi } from "../../api/Generals";
import { useMerchantStore } from "../../stores/MerchantStore";
import RenderCustomFieldGeneralForm from "../RenderComponent/RenderCustomFieldGeneralForm";
import { useParams } from "react-router-dom";
import { useMeetingStore } from "../../stores/MeetingsStore";
import { usePipelineStore } from "../../stores/PipelineStore";
import { useProductStore } from "../../stores/ProductStore";
import { ChatMerchant } from "../../interfaces/Chat/Chat";
import { useChatStore } from "../../stores/ChatStore";
import { defaultScope } from "../DataTable/UserScopeModal";
import PhoneInput from "react-phone-input-2";
import { checkWhatsapp } from "../../api/Whatsapp";
import VirtualizedSelect from "../Select/SelectVirtualized";
import { checkIsCompanyVaporesia } from "../../functions/customCase";

interface Props {
    merchant: Merchant
}

interface CityOption {
    value: string;
    label: string;
}

const UpdateMerchant: FC<Props> = ({ merchant }) => {
    tailspin.register()
    const { templateMerchantId } = useParams();
    const [checkWa, setCheckWa] = useState("")
    const [prevCountryCode, setPrevCountryCode] = useState("62");
    const { companyId, databaseMerchantId } = useCustomeStore()
    const [modalBtnLoading, setModalBtnLoading] = useState<boolean>(false);
    const [cityData, setCityData] = useState<CityOption[]>([]);
    const modalRef = useRef<HTMLDivElement | null>(null);
    const closeModalRef = useRef<HTMLButtonElement>(null);
    const token = useAccessToken()
    const { tablesData, setTablesData } = useDataTableStore()
    const { settings } = useSettingsStore()
    const [customFields, setCustomFields] = useState<CustomField>({})
    const [dataCustomFieldSerial, setDataCustomFieldSerial] = useState<CustomFieldSerialProps>({});
    const user = useUser()
    const [usersData, setUsersData] = useState<SelectOption[]>([]);
    const { selectMultiMerchant, multiMerchants, simpleMerchants } = useMerchantStore()
    const { simpleMeetings } = useMeetingStore()
    const { simplePipelines } = usePipelineStore()
    const { simpleProducts } = useProductStore()
    const [labelsOption, setLabelOption] = useState([])
    const intl = useIntl()

    const fetcher = (url: string) => axios.get(url, {
        headers: {
            'X-Auth-token': token
        }
    }).then(res => res.data.data)

    const API_URL = process.env.REACT_APP_API_URL

    const getUserURL = () => {
        if (user.data.permission_merchant_update === "ALL" && settings.is_superadmin_allowed_as_pic) {
            return `${API_URL}user/company`
        } else if (user.data.permission_merchant_update === "ALL") {
            return `${API_URL}users`
        } else {
            return `${API_URL}user/subordinates`
        }
    }

    const merchantTemplateOption: SelectOption[] = useMemo(() => {
        if (selectMultiMerchant.id && multiMerchants.length > 0) {
            return multiMerchants.map((t: SettingMultiMerchants) => ({
                label: t.name,
                value: t.id,
            }));
        }
        return [];
    }, [selectMultiMerchant, multiMerchants]);

    const { data = [], error, isLoading } = useSWR(`${API_URL}cities`, fetcher)
    const { data: users = [], error: usersError, isLoading: usersLoading } = useSWR(getUserURL(), fetcher)
    const { data: labels = [], error: labelsError, isLoading: labelsLoading } = useSWR(`${API_URL}labels?usableBy[]=merchant`, fetcher)

    const formSchema = Object.keys(customFields).reduce((obj: any, item: string) => {
        const field = customFields[item];
        const isRequiredMessage = intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: field.name });
        const isValidUrlMessage = intl.formatMessage({ id: "FORM.VALIDATION.MUST_LINK" });

        if (field.type === 'multiple' || field.type === 'multipleOption' || field.type === 'images' || field.type === 'files' || field.type === 'merchant' || field.type === 'videos' || field.type === 'user' || field.type === 'meeting' || field.type === 'product') {
            obj[item] = field.required
                ? Yup.array().min(1, isRequiredMessage).required(isRequiredMessage).nullable()
                : Yup.array().notRequired().nullable();
        } else if (field.type === 'date') {
            obj[item] = field.required
                ? Yup.string().required(isRequiredMessage).nullable()
                : Yup.string().notRequired().nullable();
        } else if (field.type === 'link') {
            obj[item] = field.required
                ? Yup.string().nullable().required(isRequiredMessage).test('is-required', isValidUrlMessage, function (value) {
                    return !value || isValidUrl(value);
                })
                : Yup.string().nullable().test('is-required', isValidUrlMessage, function (value) {
                    return !value || isValidUrl(value);
                });
        } else if (field.type === 'serial') {
            obj[item] = Yup.array().notRequired().nullable();
        } else if (field.type === 'dateTimeRange') {
            if (field.required) {
                obj[item] = Yup.array().min(1, isRequiredMessage).required(isRequiredMessage).nullable();
            } else {
                obj[item] = Yup.array().notRequired().nullable();
            }
        } else if (field.type === 'email') {
            obj[item] = field.required
                ? Yup.string().email(intl.formatMessage({ id: "FORM.VALIDATION.EMAIL" })).required(isRequiredMessage).nullable()
                : Yup.string().email(intl.formatMessage({ id: "FORM.VALIDATION.EMAIL" })).notRequired().nullable();
        } else {
            obj[item] = field.required
                ? Yup.string().required(isRequiredMessage).nullable()
                : Yup.string().notRequired().nullable();
        }

        return obj;
    }, {});

    const validationSchema = Yup.object().shape({
        merchantTemplateId: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FILTER.LABEL.DATABASE" }) })).nullable(),
        ownerId: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.PIC" }) })).nullable(),
        name: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.NAME" }) })).nullable(),
        city: user.data.company_id === companyId.elgisa ? Yup.string().notRequired().nullable() : Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.CITY" }) })).nullable(),
        email: Yup.string().email(intl.formatMessage({ id: "FORM.VALIDATION.EMAIL" })).notRequired().nullable(),
        phone: (user.data.company_id === companyId.vedaPraxis && databaseMerchantId.includes(templateMerchantId!))
            ? Yup.string().notRequired().nullable()
            : Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.PHONE" }) }))
                .test('phone-validation', intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }), function (value) {
                    if (value === prevCountryCode || !value) return this.createError({ message: intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.PHONE" }) }) });
                    return true
                }),
        labels: Yup.array().notRequired().nullable(),
        ...formSchema
    });

    const initialValues = {
        merchantTemplateId: merchant.merchantTemplateId,
        ownerId: merchant.ownerId,
        name: merchant.name,
        city: merchant.cityName,
        email: merchant.email,
        phone: merchant.phone,
        labels: merchant.labels.map((label) => { return label.id }),
        ...Object.fromEntries(Object.keys(customFields).map((key) => [key,
            merchant.customFields && merchant.customFields[key] ?
                customFields[key].type === 'date' ?
                    moment(merchant.customFields[key], ['YYYY-MM-DD', 'YYYY/MM/DD'], true).isValid() ?
                        merchant.customFields[key]
                        : null
                    : customFields[key].type === 'number' ?
                        customThousandSeparatorDot(merchant.customFields[key] as unknown as number)
                        : merchant.customFields[key]
                : null]
        )),
    };

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: async (values, { setStatus, setSubmitting }) => {
            setModalBtnLoading(true)

            try {
                await checkWhatsapp(values.phone.replace("+", "")).then(async (valCheckWa) => {
                    const customFieldsValues: Record<string, string | string[] | number | null> = Object.keys(customFields).reduce(
                        (obj, item) => ({
                            ...obj,
                            [item]: (values as Record<string, string | string[] | number | null>)[item],
                        }),
                        {}
                    );

                    const convertCustomFields: Record<string, any> = Object.keys(customFields).reduce(
                        (obj, item) => {
                            if (!obj.info_custom_fields) {
                                obj.info_custom_fields = {};
                            }

                            if (customFields[item].type === 'merchant') {
                                const selectedMerchants = getDataById(values[item as keyof typeof values] as any, simpleMerchants);
                                obj[`custom_fields.${item}`] = selectedMerchants?.map((merchant: any) => merchant.name).join(", ");
                                obj.info_custom_fields[item] = selectedMerchants?.map((merchant: any) => ({
                                    merchant_template_id: merchant.merchantTemplateId,
                                    merchant_id: merchant.id,
                                    merchant_name: merchant.name
                                })) || [];
                            }
                            else if (customFields[item].type === 'user') {
                                const selectedUsers = getDataById(values[item as keyof typeof values] as any, users);
                                obj[`custom_fields.${item}`] = selectedUsers?.map((user: any) => user.name).join(", ");
                                obj.info_custom_fields[item] = selectedUsers?.map((user: any) => ({
                                    id: user.id,
                                    name: user.name
                                })) || [];
                            }
                            else if (customFields[item].type === 'pipeline') {
                                const selectedPipeline = getDataById(values[item as keyof typeof values] as string, simplePipelines);
                                obj[`custom_fields.${item}`] = selectedPipeline ?
                                    `${selectedPipeline.name} / ${selectedPipeline.nominal} / ${moment(selectedPipeline.dateCreated).format('DD MMM YYYY')}`
                                    : '-';
                                obj.info_custom_fields[item] = selectedPipeline ? [{
                                    id: selectedPipeline.id,
                                    name: selectedPipeline.name,
                                    nominal: selectedPipeline.nominal,
                                    dateCreated: selectedPipeline.dateCreated
                                }] : [];
                            }
                            else if (customFields[item].type === 'meeting') {
                                const selectedMeeting: any[] = getDataById(values[item as keyof typeof values] as string, simpleMeetings);
                                obj[`custom_fields.${item}`] = selectedMeeting && selectedMeeting.length > 0 ?
                                    selectedMeeting.map((meeting: any) => `${meeting.name} / ${moment(meeting.dateMeet).format('DD MMM YYYY')} / ${meeting.meetingStatus}`).join(', ')
                                    : '-';
                                obj.info_custom_fields[item] = selectedMeeting && selectedMeeting.length > 0 ? selectedMeeting.map((meeting: any) => ({
                                    meeting_template_id: meeting.meetingTemplateId,
                                    id: meeting.id,
                                    merchant_name: meeting.name,
                                    date_meet: meeting.dateMeet,
                                    meeting_status: meeting.meetingStatus
                                })) : [];
                            } else if (customFields[item].type === 'product') {
                                const selectedProducts = getDataById(values[item as keyof typeof values] as any, simpleProducts);
                                obj[`custom_fields.${item}`] = selectedProducts?.map((product: any) => product.name).join(", ");
                                obj.info_custom_fields[item] = selectedProducts?.map((product: any) => ({
                                    product_id: product.id,
                                    product_name: product.name,
                                    product_prices: product.product_prices,
                                })) || [];
                            }
                            else {
                                obj[`custom_fields.${item}`] = values[item as keyof typeof values] || '';
                                obj.info_custom_fields[item] = [];
                            }

                            return obj;
                        },
                        {} as Record<string, any>
                    );

                    var countResourceError = 0
                    if (values.phone.startsWith('0')) {
                        values.phone = '62' + values.phone.substring(1);
                    }

                    const getWaCheck = valCheckWa.data.result.exists;
                    const getStatusCode = valCheckWa.status;
                    const isWhatsappPhone = getStatusCode === 200 ? getWaCheck : null;

                    await Promise.all(
                        Object.keys(customFields).map(async (key) => {
                            if (customFields[key].type === 'image' || customFields[key].type === 'file' || customFields[key].type === 'video') {
                                try {
                                    if (values[key as keyof typeof values] && typeof values[key as keyof typeof values] === 'object') {
                                        const resourceResponse = await insertAttachment(values[key as keyof typeof values] as string | File, 'MERCHANT', token);
                                        if (resourceResponse.data.success) customFieldsValues[key] = resourceResponse.data.data.length > 0 ? resourceResponse.data.data[0] : null;
                                        else countResourceError++
                                    }
                                } catch (error) {
                                    countResourceError++
                                }
                            } else if (customFields[key].type === 'images' || customFields[key].type === 'files' || customFields[key].type === 'videos') {
                                try {
                                    if (values[key as keyof typeof values]) {
                                        var dataMultiAttachment = (values[key as keyof typeof values] as any).filter((item: any) => typeof item !== 'string')
                                        var dataUrlAttachment = (values[key as keyof typeof values] as any).filter((item: any) => typeof item === 'string')

                                        const resourceResponse = await insertMultiAttachment(dataMultiAttachment, 'MERCHANT', token);
                                        if (resourceResponse.data.success) (customFieldsValues[key] as any) = resourceResponse.data.data.length > 0 ? [...dataUrlAttachment, ...resourceResponse.data.data] : dataUrlAttachment.length > 0 ? dataUrlAttachment : null;
                                        else countResourceError++
                                    }
                                } catch (error) {
                                    countResourceError++
                                }
                            } else if (customFields[key].type === 'multiple' || customFields[key].type === 'multipleOption') {
                                var multipleValues = values[key as keyof typeof values]?.length! > 0 ? values[key as keyof typeof values] : null;
                                customFieldsValues[key] = multipleValues as string
                            } else if (customFields[key].type === 'serial') {
                                try {
                                    if (dataCustomFieldSerial[key].value.length === 0) {
                                        const serialResponse = await generateSerialApi(key, "Merchant", token);
                                        customFieldsValues[key] = serialResponse.data.success ? serialResponse.data.data : null;
                                    } else {
                                        customFieldsValues[key] = dataCustomFieldSerial[key].value
                                    }
                                } catch (error) {
                                    countResourceError++
                                }
                            } else if (customFields[key].type === 'number') {
                                const stringValue = values[key as keyof typeof values] as string;
                                customFieldsValues[key] = stringValue ? unformatNumberInput(stringValue) : null;
                            }
                        })
                    );

                    if (countResourceError > 0) {
                        Swal.fire({
                            icon: "error",
                            text: `${intl.formatMessage({ id: "FORM.VALIDATION.UPDATE_FAILED" })}.`,
                            heightAuto: false,
                            confirmButtonText: 'Ok'
                        })
                        setModalBtnLoading(false);
                    } else {
                        try {
                            const merchantLabels = values.labels.length > 0 && Array.isArray(values.labels) ? values.labels.map((label) => { return { id: label } }) : []

                            const response = await updateMerchant(
                                values.merchantTemplateId,
                                merchantLabels,
                                values.ownerId,
                                merchant.id,
                                values.name,
                                values.city,
                                values.phone.replace("+", ""),
                                isWhatsappPhone,
                                token,
                                values.email ? values.email : null,
                                customFieldsValues
                            )
                            if (response.data.success) {
                                Swal.fire({
                                    icon: 'success',
                                    heightAuto: false,
                                    title: intl.formatMessage({ id: 'FORM.VALIDATION.UPDATE_SUCCESS' }),
                                    timer: 2000,
                                    timerProgressBar: true
                                })
                                closeModalRef.current?.click();
                                mutate(`${API_URL}simple-merchants`)
                                if (window.location.pathname === '/chat') {
                                    const { setListChatRooms, phoneNumber } = useChatStore.getState();
                                    let newMerchant: ChatMerchant = {
                                        PIC: {
                                            id: response.data.data.ownerId,
                                            name: response.data.data.ownerName
                                        },
                                        id: response.data.data.id,
                                        name: response.data.data.name,
                                        phone: response.data.data.phone,
                                        template: {
                                            id: response.data.data.merchantTemplateId,
                                            name: response.data.data.merchantTemplateName
                                        }
                                    }

                                    setListChatRooms((prevMessages) => {
                                        const currentRoom = prevMessages[phoneNumber];
                                        if (!currentRoom) return prevMessages;

                                        const updatedMerchants = currentRoom.merchants.map((merchant) =>
                                            merchant.id === newMerchant.id ? newMerchant : merchant
                                        );

                                        return {
                                            ...prevMessages,
                                            [phoneNumber]: {
                                                ...currentRoom,
                                                merchants: updatedMerchants,
                                            },
                                        };
                                    });

                                    const getMerchantURL = () => {
                                        if (user.data.is_superadmin) {
                                            return `${API_URL}merchants?scope=all&phone=${phoneNumber.split('-')[0]}`
                                        } else {
                                            return `${API_URL}merchants?scope=${defaultScope(user.data.permission_merchant_read)}&user_id=${user.data.id}&phone=${phoneNumber.split('-')[0]}`
                                        }
                                    }

                                    mutate(getMerchantURL())
                                } else {
                                    setTablesData('merchants', tablesData.merchants
                                        .map(item =>
                                            item.id === merchant.id
                                                ? {
                                                    ...item,
                                                    ...convertCustomFields.custom_fields,
                                                    merchantTemplateId: response.data.data.merchantTemplateId,
                                                    info_custom_fields: convertCustomFields.info_custom_fields,
                                                    customFields: response.data.data.customFields,
                                                    name: response.data.data.name,
                                                    cityName: response.data.data.cityName,
                                                    ownerId: response.data.data.ownerId,
                                                    ownerName: response.data.data.ownerName,
                                                    phone: response.data.data.phone,
                                                    email: response.data.data.email,
                                                    labels: response.data.data.labels,
                                                }
                                                : item
                                        )
                                        .filter(item => item.merchantTemplateId === selectMultiMerchant.id)
                                    );
                                }
                            }
                        } catch (error: any) {
                            Swal.fire({
                                icon: 'error',
                                title: error.response.data.message,
                                confirmButtonText: 'Ok'
                            })
                        } finally {
                            setModalBtnLoading(false)
                        }
                    }
                })
            } catch (error: any) {
                Swal.fire({
                    icon: 'error',
                    title: error.response.data.message,
                    heightAuto: false,
                    confirmButtonText: 'Ok'
                });
            } finally {
                setModalBtnLoading(false);
            }

        }
    });

    useEffect(() => {
        const modalElement = modalRef.current;

        const handleModalHidden = () => {
            setCheckWa("")
            formik.resetForm();
        };

        const handleModalShown = () => {
            const matchingCity = cityData.find(
                (city: CityOption) => city.label?.toLowerCase() === merchant.cityName?.toLowerCase()
            );

            if (matchingCity) {
                formik.setFieldValue("city", matchingCity.value);
            }
        };

        modalElement?.addEventListener('hidden.bs.modal', handleModalHidden);
        modalElement?.addEventListener('shown.bs.modal', handleModalShown);

        return () => {
            modalElement?.removeEventListener('hidden.bs.modal', handleModalHidden);
            modalElement?.removeEventListener('shown.bs.modal', handleModalShown);
        };
    }, [formik, cityData, merchant.cityName]);

    useEffect(() => {
        if (!isLoading) {
            const cityData = data.map((city: City) => ({
                value: city.id,
                label: city.name
            }));
            cityData.sort((a: CityOption, b: CityOption) => a.label.localeCompare(b.label));
            setCityData(cityData);
        }
    }, [data]);

    useEffect(() => {
        const newLabelsOption = labels.map((label: any) => { return { value: label.id, label: label.name } })
        setLabelOption(newLabelsOption)
    }, [labels])

    useEffect(() => {
        if (!usersLoading) {
            const usersData = users.map((user: User) => ({
                value: user.id,
                label: user.name
            }));

            if (user.data.permission_merchant_update !== 'ALL') {
                usersData.unshift({ value: user.data.id, label: user.data.name })
            }
            setUsersData(usersData)
        }
    }, [users])

    useEffect(() => {
        if (Object.keys(selectMultiMerchant).length !== 0) {
            var data = selectMultiMerchant.customFields
            const transformedData = Object.keys(data).reduce((acc: any, key: string) => {
                acc[key] = {
                    loading: false,
                    value: []
                };
                return acc;
            }, {});

            setCustomFields(data)
            setDataCustomFieldSerial(transformedData)
        }
    }, [selectMultiMerchant])

    const handleGenerateSerial = async (key: string) => {
        setDataCustomFieldSerial(prevState => ({
            ...prevState,
            [key]: { ...prevState[key], loading: true }
        }));

        try {
            const response = await generateSerialApi(key, "Merchant", token);
            if (response.data.success) {
                formik.setFieldValue(key, response.data.data)
                setDataCustomFieldSerial(prevState => ({
                    ...prevState,
                    [key]: { ...prevState[key], value: response.data.data }
                }));
            }
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: error.response.data.message,
                confirmButtonText: 'Ok',
                target: document.getElementById('update-merchant-modal'),
                heightAuto: false,
            })
        } finally {
            setDataCustomFieldSerial(prevState => ({
                ...prevState,
                [key]: { ...prevState[key], loading: false }
            }));
        }
    }

    const checkWaValid = async (phone: string) => {
        setCheckWa("loading")
        try {
            const response = await checkWhatsapp(phone.replace("+", ""))
            setCheckWa(response.data.result.exists ? "true" : "false")
            return response.data.result.exists ? "true" : "false";
        } catch (error) {

        }
    }

    const { handleCloseModal } = useHandleCloseModal(modalRef, closeModalRef, formik);

    return (
        <div className="modal fade" tabIndex={-1} id={`update-merchant-modal`} ref={modalRef}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{intl.formatMessage({ id: 'FORM.ACTION.UPDATE' })} {merchant.name}</h5>
                        <div
                            className="btn btn-active-light-primary btn-icon btn-sm ms-2"
                            onClick={handleCloseModal}
                        >
                            <KTSVG
                                path="/media/icons/duotune/arrows/arr061.svg"
                                className="svg-icon svg-icon-2x"
                            />
                        </div>
                    </div>
                    <form onSubmit={formik.handleSubmit} noValidate>
                        <div className="modal-body">
                            {formik.status && (
                                <div className='alert alert-danger mb-lg-15'>
                                    <div className='alert-text font-weight-bold'>{formik.status}</div>
                                </div>
                            )}
                            <label className='form-label text-dark fs-6 fw-bolder required'>{intl.formatMessage({ id: 'FORM.LABEL.TEMPLATE_TITLE' }, { title: settings.merchant_title })}</label>
                            <div className="input-group mb-3">
                                {
                                    <VirtualizedSelect
                                        options={merchantTemplateOption}
                                        placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${intl.formatMessage({ id: 'FORM.LABEL.TEMPLATE_TITLE' }, { title: settings.merchant_title })}`}
                                        value={merchantTemplateOption.find(option => option.value === formik.values.merchantTemplateId) || null}
                                        onChange={(option) => {
                                            formik.handleChange({
                                                target: {
                                                    name: "merchantTemplateId",
                                                    value: option?.value || ""
                                                }
                                            })
                                        }}
                                        onBlur={
                                            () => formik.values.merchantTemplateId === "" && formik.setFieldTouched("merchantTemplateId", true)
                                        }
                                        className="w-100 z-index-3"
                                    />
                                }
                            </div>
                            {formik.touched.merchantTemplateId && formik.errors.merchantTemplateId && (
                                <div className='text-danger fv-plugins-message-container'>
                                    <span role='alert' className="text-danger">{formik.errors.merchantTemplateId}</span>
                                </div>
                            )}
                            {/* Users */}
                            <label className='form-label text-dark fs-6 fw-bolder required'>{intl.formatMessage({ id: 'FORM.LABEL.PIC' })}</label>
                            <div className="input-group mb-3">
                                {
                                    user.data.permission_merchant_update === 'TEAM' || user.data.permission_merchant_update === 'ALL' ?
                                        <Select
                                            key={
                                                window.location.pathname === '/chat' ? nanoid() : ''
                                            }
                                            options={usersData}
                                            placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${intl.formatMessage({ id: 'FORM.LABEL.PIC' })}`}
                                            value={usersData.find(option => option.value === formik.values.ownerId)}
                                            onChange={
                                                option => formik.handleChange({
                                                    target: {
                                                        name: "ownerId",
                                                        value: option?.value
                                                    }
                                                })
                                            }
                                            onBlur={
                                                () => formik.values.ownerId === "" && formik.setFieldTouched("ownerId", true)
                                            }
                                            className="w-100 z-index-2"
                                            isDisabled={usersLoading}
                                        />
                                        :
                                        <div className="input-group mb-3">
                                            <input
                                                className={clsx(
                                                    'form-control form-control-lg',
                                                )}
                                                type='text'
                                                name='ownerId'
                                                autoComplete='off'
                                                onInput={(e) => handleCaps(e)}
                                                value={merchant.ownerName}
                                                disabled
                                            />
                                        </div>
                                }
                            </div>
                            {formik.touched.ownerId && formik.errors.ownerId && (
                                <div className='text-danger fv-plugins-message-container'>
                                    <span role='alert' className="text-danger">{formik.errors.ownerId}</span>
                                </div>
                            )}
                            <label className='form-label text-dark fs-6 fw-bolder required'>{intl.formatMessage({ id: 'FORM.LABEL.NAME' })}</label>
                            <div className="input-group mb-3">
                                <input
                                    {...formik.getFieldProps('name')}
                                    className={clsx(
                                        'form-control form-control-lg',
                                        { 'is-invalid': formik.touched.name && formik.errors.name },
                                    )}
                                    type='text'
                                    name='name'
                                    autoComplete='off'
                                    onInput={(e) => handleCaps(e)}
                                />
                            </div>
                            {formik.touched.name && formik.errors.name && (
                                <div className='text-danger fv-plugins-message-container'>
                                    <span role='alert' className="text-danger">{formik.errors.name}</span>
                                </div>
                            )}
                            {/* City */}
                            <label className={`form-label fs-6 fw-bolder text-dark ${user.data.company_id !== companyId.elgisa && "required"}`}>{intl.formatMessage({ id: 'FORM.LABEL.CITY' })}</label>
                            <div className="input-group mb-3">
                                <Select
                                    options={cityData}
                                    placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${intl.formatMessage({ id: 'FORM.LABEL.CITY' })}`}
                                    value={cityData.find(option => option.value === formik.values.city)}
                                    onChange={
                                        option => formik.handleChange({
                                            target: {
                                                name: "city",
                                                value: option?.value
                                            }
                                        })
                                    }
                                    onBlur={
                                        () => formik.values.city === "" && formik.setFieldTouched("city", true)
                                    }
                                    className="w-100 z-index-1"
                                    isDisabled={isLoading}
                                />
                            </div>
                            {formik.touched.city && formik.errors.city && (
                                <div className='text-danger fv-plugins-message-container'>
                                    <span role='alert' className="text-danger">{formik.errors.city}</span>
                                </div>
                            )}

                            {
                                window.location.pathname !== '/chat' &&
                                <>
                                    <label className={`form-label text-dark fs-6 fw-bolder ${(user.data.company_id === companyId.vedaPraxis && databaseMerchantId.includes(templateMerchantId!)) ? "" : "required"}`}>{intl.formatMessage({ id: 'FORM.LABEL.PHONE' })}</label>
                                    {
                                        checkWa === "true" ?
                                            <span className="text-success" style={{ float: "inline-end" }}>{intl.formatMessage({ id: "FORM.VALIDATION.VALID_WHATSAPP" })}</span>
                                            : checkWa === "false" ?
                                                <span className="text-danger" style={{ float: "inline-end" }}>{intl.formatMessage({ id: "FORM.VALIDATION.INVALID_WHATSAPP" })}</span>
                                                : checkWa === "loading" ?
                                                    <span className="align-middle spinner-border spinner-border-sm" style={{ float: "inline-end" }}></span>
                                                    : <></>
                                    }
                                    <div style={{ marginBottom: "0.75rem" }}>
                                        <PhoneInput
                                            countryCodeEditable={false}
                                            country="id"
                                            value={formik.values.phone}
                                            onChange={(value, country: any) => {
                                                setPrevCountryCode(country.dialCode);
                                                formik.setFieldValue('phone', value);
                                            }}
                                            onBlur={() => checkWaValid(formik.values.phone)}
                                            enableSearch={true}
                                            inputStyle={{ width: "100%" }}
                                        />
                                    </div>
                                    {formik.touched.phone && formik.errors.phone && (
                                        <div className='text-danger fv-plugins-message-container'>
                                            <span role='alert' className="text-danger">{formik.errors.phone}</span>
                                        </div>
                                    )}
                                </>
                            }
                            {/* label */}
                            {
                                selectMultiMerchant.isFeatureLabelEnabled &&
                                <>
                                    <label className='form-label text-dark fs-6 fw-bolder'>{intl.formatMessage({ id: 'FORM.LABEL.LABELS' })}</label>
                                    <div className="input-group mb-3">
                                        <Select
                                            closeMenuOnSelect={false}
                                            openMenuOnClick
                                            isMulti
                                            options={labelsOption}
                                            placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${intl.formatMessage({ id: 'FORM.LABEL.LABELS' })}`}
                                            value={labelsOption.filter((option: any) =>
                                                formik.values.labels.includes(option.value)
                                            )
                                            }
                                            onChange={(selectedOptions) => {
                                                const selectedValues = selectedOptions
                                                    ? selectedOptions.map((option: any) => option.value)
                                                    : []
                                                formik.setFieldValue('labels', selectedValues)
                                            }}
                                            className='w-100'
                                            onBlur={() =>
                                                formik.values.labels.length == 0 &&
                                                formik.setFieldTouched('labels', true)
                                            }
                                        />
                                    </div>
                                    {formik.touched.labels && formik.errors.labels && (
                                        <div className='text-danger fv-plugins-message-container'>
                                            <span role='alert' className="text-danger">{formik.errors.labels}</span>
                                        </div>
                                    )}
                                </>
                            }
                            {
                                !checkIsCompanyVaporesia(companyId, user) && (
                                    <>
                                        <label className='form-label text-dark fs-6 fw-bolder'>Email</label>
                                        <div className="input-group mb-3">
                                            <input
                                                placeholder='Email'
                                                {...formik.getFieldProps('email')}
                                                className={clsx(
                                                    'form-control form-control-lg txt-email',
                                                    { 'is-invalid': formik.touched.email && formik.errors.email }
                                                )}
                                                type='email'
                                                name='email'
                                                autoComplete='off'
                                            />
                                        </div>
                                        {formik.touched.email && formik.errors.email && (
                                            <div className='text-danger fv-plugins-message-container'>
                                                <span role='alert' className="text-danger">{formik.errors.email}</span>
                                            </div>
                                        )}
                                    </>
                                )
                            }
                            {
                                customFields &&
                                <RenderCustomFieldGeneralForm
                                    customFieldGeneral={customFields}
                                    formik={formik}
                                    errors={formik.errors}
                                    containerRef={document.querySelector(`#update-merchant-modal`) as HTMLElement}
                                    dataCustomFieldSerial={dataCustomFieldSerial}
                                    handleGenerateSerial={handleGenerateSerial}
                                    typeAction='update'
                                />
                            }
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="d-none btn btn-light"
                                data-bs-dismiss="modal"
                                ref={closeModalRef}
                            >
                                {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                            </button>
                            <button
                                type="button"
                                className="btn btn-light"
                                onClick={handleCloseModal}
                            >
                                {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                            </button>
                            <button type="submit" className={`btn btn-primary me-10 ${modalBtnLoading ? "disabled" : ""}`} data-kt-indicator={modalBtnLoading ? 'on' : 'off'}>
                                <span className="indicator-label">
                                    {intl.formatMessage({ id: 'FORM.ACTION.UPDATE' })}
                                </span>
                                <span className="indicator-progress">
                                    Loading... <span className="align-middle ms-2 spinner-border spinner-border-sm"></span>
                                </span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export { UpdateMerchant }